import { notification } from 'antd';
import { autocomplete, disclosureSearch, fetchInspectionReportDetail } from '@/pages/mhra_disclosurelog/list/service';
import { AutoCompleteParams, TableListData, MhraInspectionReportDetail } from '@/pages/mhra_disclosurelog/list/data';

export interface MhraDisclosureLogTableListStateType {
  data: TableListData;
  isFiltering: boolean;
  autoCompleteData: AutoCompleteParams;
  detail: MhraInspectionReportDetail | null;
  detailLoading: boolean;
}

let residualSearch: any;

function isPaginationOnlyPayload(payload: any): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const hasPaginationKeys =
    payload.currentPage !== undefined ||
    payload.current !== undefined ||
    payload.pageSize !== undefined ||
    payload.start !== undefined ||
    payload.length !== undefined;
  const hasSearchOrFilter = payload.search !== undefined || payload.filter !== undefined;
  return hasPaginationKeys && !hasSearchOrFilter;
}

export default {
  namespace: 'mhraDisclosureLogTableList',

  state: {
    data: {
      list: [],
      pagination: {},
    },
    isFiltering: false,
    autoCompleteData: {
      search: '',
      dataSource: [],
    },
    detail: null,
    detailLoading: false,
  },

  effects: {
    *fetch({ payload }: any, { call, put }: any): any {
      const body: any = { ...(payload || {}) };
      if (!Array.isArray(body.filter)) {
        body.filter = [];
      }
      let response;
      if (isPaginationOnlyPayload(body) && residualSearch) {
        response = yield call(disclosureSearch, {
          ...residualSearch,
          pagination: body,
        });
      } else if (body.searchField || body.filterField) {
        if (body.pagination) {
          residualSearch = body;
          response = yield call(disclosureSearch, body);
        } else {
          residualSearch.pagination = body;
          response = yield call(disclosureSearch, residualSearch);
        }
      } else {
        const fetchObj = {
          search: { none: '' },
          filter: [],
          searchField: true,
          filterField: true,
          pagination: body,
        };
        response = yield call(disclosureSearch, fetchObj);
      }

      if (response && response.status === 200) {
        yield put({
          type: 'save',
          payload: response,
        });
      } else {
        notification.error({
          message: 'Error while fetching MHRA inspection reports',
        });
      }
    },

    *fetchDetail({ payload }: any, { call, put }: any): any {
      yield put({ type: 'setDetailLoading', payload: true });
      const response = yield call(fetchInspectionReportDetail, payload.reportId);
      if (response && response.dbid) {
        yield put({
          type: 'saveDetail',
          payload: response,
        });
      } else {
        notification.error({
          message: 'Error while fetching inspection report detail',
        });
      }
      yield put({ type: 'setDetailLoading', payload: false });
    },

    *autoComplete({ payload }: any, { call, put }: any): any {
      const response = yield call(autocomplete, payload);
      if (response) {
        yield put({
          type: 'saveEntries',
          payload: response,
        });
      }
    },

    *clearResidual(_: any, { put }: any): any {
      if (residualSearch !== undefined) {
        residualSearch.search.none = '';
        residualSearch.filter = [];
      }
      yield put({ type: 'clearDetail' });
    },

    *toggleFilter(_: any, { put }: any): any {
      yield put({
        type: 'toggleIsFiltering',
      });
    },

    *setFilter({ payload }: any, { put }: any): any {
      yield put({
        type: 'setIsFiltering',
        payload,
      });
    },
  },

  reducers: {
    save(state: any, action: any) {
      const payload = action.payload || {};
      const pag = payload.pagination || {};
      const data = {
        ...payload,
        pagination: {
          ...pag,
          current: pag.currentPage ?? pag.current,
        },
      };
      return {
        ...state!,
        data,
        autoCompleteData: { ...state!.autoCompleteData, dataSource: [] },
      };
    },

    saveEntries(state: any, action: any) {
      return {
        ...state!,
        autoCompleteData: { ...state!.autoCompleteData, dataSource: action.payload },
      };
    },

    saveDetail(state: any, action: any) {
      return {
        ...state!,
        detail: action.payload,
      };
    },

    clearDetail(state: any) {
      return {
        ...state!,
        detail: null,
      };
    },

    setDetailLoading(state: any, action: any) {
      return {
        ...state!,
        detailLoading: Boolean(action.payload),
      };
    },

    toggleIsFiltering(state: any) {
      return {
        ...state!,
        isFiltering: !state!.isFiltering,
      };
    },

    setIsFiltering(state: any, action: any) {
      return {
        ...state!,
        isFiltering: Boolean(action.payload),
      };
    },
  },
};
